/**
 * Collapsible two-octave piano (SPEC §6.7), C4–C6. Plays with the mouse and
 * with computer keys `a w s e d f t g y h u j k o l p ;` mapped from C4
 * (Shift = accent, velocity 1.0). Keys light on both inputs. Computer keys
 * are only active while the piano or the sequencer grid has focus, or when
 * nothing focusable is focused; they are ignored in inputs / selects /
 * textareas and with Ctrl / Cmd / Alt held.
 */
import type { Controller } from '../app/controller';
import { h, injectStyle, pitchLabel, setFlag } from './dom';

/** Dependencies for `mountPiano()`; only note on/off is needed from the controller. */
export interface PianoDeps {
  controller: Pick<Controller, 'noteOn' | 'noteOff'>;
}

/** Mounted piano: root element, focus-gate probe, expand/collapse, dispose. */
export interface PianoHandle {
  el: HTMLElement;
  /** True when computer keys currently reach the piano. */
  isActive(): boolean;
  setExpanded(open: boolean): void;
  dispose(): void;
}

/** Lowest pitch rendered (C4). */
export const PIANO_LOW = 60;
/** Highest pitch rendered (C6). */
export const PIANO_HIGH = 84;

/** Computer-key → MIDI pitch, mapped from C4 (SPEC §6.7). */
export const KEY_TO_PITCH: Readonly<Record<string, number>> = {
  a: 60, w: 61, s: 62, e: 63, d: 64, f: 65, t: 66, g: 67, y: 68, h: 69, u: 70, j: 71, k: 72, o: 73, l: 74, p: 75, ';': 76,
};

const CODE_TO_KEY: Readonly<Record<string, string>> = {
  KeyA: 'a', KeyW: 'w', KeyS: 's', KeyE: 'e', KeyD: 'd', KeyF: 'f', KeyT: 't', KeyG: 'g', KeyY: 'y', KeyH: 'h', KeyU: 'u', KeyJ: 'j',
  KeyK: 'k', KeyO: 'o', KeyL: 'l', KeyP: 'p', Semicolon: ';',
};

const PITCH_TO_KEY: Readonly<Record<number, string>> = Object.fromEntries(Object.entries(KEY_TO_PITCH).map(([k, p]) => [p, k]));

/** MIDI pitch for a keyboard key (case-insensitive; `:` counts as `;`), or null. */
export function pitchForKey(key: string): number | null {
  const k = key === ':' ? ';' : key.length === 1 ? key.toLowerCase() : key;
  return Object.prototype.hasOwnProperty.call(KEY_TO_PITCH, k) ? KEY_TO_PITCH[k]! : null;
}

/** Resolve the logical key from a KeyboardEvent (physical code first, so Shift/layout do not matter). */
export function keyFromEvent(e: KeyboardEvent): string {
  return CODE_TO_KEY[e.code] ?? (e.key === ':' ? ';' : e.key.length === 1 ? e.key.toLowerCase() : e.key);
}

const BLACK = new Set([1, 3, 6, 8, 10]);

const CSS = `
.bm-piano{border:1px solid var(--border);border-radius:var(--radius-l);background:var(--surface-1);min-width:0}
.bm-piano__head{display:flex;align-items:center;gap:.6rem;padding:.4rem .6rem;flex-wrap:wrap}
.bm-piano__toggle{display:inline-flex;align-items:center;gap:.4rem;background:none;border:0;padding:.2rem .3rem;border-radius:var(--radius-s);
  color:var(--fg);font-weight:600;cursor:pointer}
.bm-piano__toggle::before{content:"▸";display:inline-block;color:var(--fg-muted);transition:transform .15s var(--ease)}
.bm-piano__toggle[aria-expanded="true"]::before{transform:rotate(90deg)}
.bm-piano__hint{font-size:.75rem;color:var(--fg-muted)}
.bm-piano__hint kbd{margin-right:.1em}
.bm-piano__panel{padding:0 .6rem .6rem}
.bm-piano__keys{position:relative;height:clamp(84px,14vw,120px);display:flex;gap:2px;user-select:none;-webkit-user-select:none;touch-action:none}
.bm-key{border:1px solid var(--border-strong);padding:0;cursor:pointer;position:relative;display:flex;align-items:flex-end;justify-content:center;
  font-size:.7rem;color:var(--fg-faint);transition:background-color .05s linear}
.bm-key--white{flex:1 1 0;min-width:0;height:100%;background:#efe9df;color:#5d564c;border-radius:0 0 5px 5px}
.bm-key--white:hover{background:#fbf6ec}
.bm-key--black{position:absolute;top:0;width:calc(100% / 15 * .62);height:58%;background:#232019;color:var(--fg-faint);border-radius:0 0 4px 4px;
  z-index:2;box-shadow:0 3px 0 rgba(0,0,0,.4)}
.bm-key--black:hover{background:#312c25}
.bm-key[data-on="true"]{background:var(--human) !important;color:var(--human-fg) !important;border-color:var(--human)}
.bm-key[data-tonic]{box-shadow:inset 0 -3px 0 var(--agent-soft)}
.bm-key__label{pointer-events:none;padding-bottom:.25rem;line-height:1}
.bm-key--black .bm-key__label{padding-bottom:.2rem;font-size:.6rem}
`;

/** Mount the piano into `container`. */
export function mountPiano(container: HTMLElement, deps: PianoDeps): PianoHandle {
  injectStyle('bm-piano-css', CSS);
  const { controller } = deps;
  const keys = new Map<number, HTMLButtonElement>();
  const keysRow = h('div', { class: 'bm-piano__keys', role: 'group', 'aria-label': 'Piano keys C4 to C6' });

  const whiteCount = 15;
  let whiteIndex = 0;
  for (let pitch = PIANO_LOW; pitch <= PIANO_HIGH; pitch++) {
    const black = BLACK.has(pitch % 12);
    const label = PITCH_TO_KEY[pitch];
    const btn = h(
      'button',
      {
        type: 'button',
        class: `bm-key ${black ? 'bm-key--black' : 'bm-key--white'}`,
        dataset: { pitch: String(pitch) },
        'aria-label': pitchLabel(pitch),
        tabindex: -1,
        title: label ? `${pitchLabel(pitch)} (key ${label})` : pitchLabel(pitch),
      },
      h('span', { class: 'bm-key__label', 'aria-hidden': 'true' }, label ?? (pitch % 12 === 0 ? pitchLabel(pitch) : '')),
    ) as HTMLButtonElement;
    if (pitch % 12 === 0) btn.dataset.tonic = 'true';
    if (black) btn.style.left = `calc(${(whiteIndex / whiteCount) * 100}% - (100% / ${whiteCount} * .31))`;
    else whiteIndex++;
    keys.set(pitch, btn);
    keysRow.append(btn);
  }

  const lit = (pitch: number, on: boolean) => {
    const k = keys.get(pitch);
    if (k) setFlag(k, 'data-on', on, 'true');
  };
  const on = (pitch: number, accent: boolean) => {
    controller.noteOn(pitch, accent ? 1 : 0.8);
    lit(pitch, true);
  };
  const off = (pitch: number) => {
    controller.noteOff(pitch);
    lit(pitch, false);
  };

  // ---------- mouse / touch ----------
  const pointerPitches = new Map<number, number>(); // pointerId → pitch
  const pitchOf = (t: EventTarget | null) => {
    const b = (t as HTMLElement | null)?.closest?.('[data-pitch]') as HTMLElement | null;
    return b ? Number(b.dataset.pitch) : null;
  };
  keysRow.addEventListener('pointerdown', (e) => {
    const pitch = pitchOf(e.target);
    if (pitch == null) return;
    e.preventDefault();
    pointerPitches.set(e.pointerId ?? 0, pitch);
    on(pitch, e.shiftKey);
  });
  const release = (e: PointerEvent) => {
    const id = e.pointerId ?? 0;
    const pitch = pointerPitches.get(id);
    if (pitch == null) return;
    pointerPitches.delete(id);
    off(pitch);
  };
  keysRow.addEventListener('pointerup', release);
  keysRow.addEventListener('pointercancel', release);
  keysRow.addEventListener('pointerleave', release);
  // Glissando: sliding onto another key while held re-triggers.
  keysRow.addEventListener('pointerover', (e) => {
    const id = e.pointerId ?? 0;
    const held = pointerPitches.get(id);
    if (held == null || !(e.buttons & 1)) return;
    const pitch = pitchOf(e.target);
    if (pitch == null || pitch === held) return;
    off(held);
    pointerPitches.set(id, pitch);
    on(pitch, e.shiftKey);
  });
  keysRow.addEventListener('contextmenu', (e) => e.preventDefault());

  // ---------- computer keys ----------
  const isTextField = (t: Element | null) =>
    !!t && (t.matches('input, select, textarea, [contenteditable=""], [contenteditable="true"]') || !!t.closest('[contenteditable=""], [contenteditable="true"]'));
  const isActive = (): boolean => {
    const a = document.activeElement;
    if (!a || a === document.body || a === document.documentElement) return true;
    if (el.contains(a)) return true;
    if (isTextField(a)) return false;
    // Inside a modal / dialog (ask card, audition card) letters belong to that dialog.
    if (a.closest('[role="dialog"], [role="alertdialog"], [data-no-piano]')) return false;
    // Buttons, tabs, chips, grid cells: none of them consume letter keys, so the piano stays live.
    return true;
  };
  const downKeys = new Map<string, number>();
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.repeat || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
    const k = keyFromEvent(e);
    const pitch = pitchForKey(k);
    if (pitch == null || downKeys.has(k) || !isActive()) return;
    e.preventDefault();
    downKeys.set(k, pitch);
    on(pitch, e.shiftKey);
  };
  const onKeyUp = (e: KeyboardEvent) => {
    const k = keyFromEvent(e);
    const pitch = downKeys.get(k);
    if (pitch == null) return;
    downKeys.delete(k);
    off(pitch);
  };
  const releaseAll = () => {
    for (const pitch of downKeys.values()) off(pitch);
    downKeys.clear();
    for (const pitch of pointerPitches.values()) off(pitch);
    pointerPitches.clear();
  };
  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', releaseAll);

  // ---------- collapsible shell ----------
  const panel = h('div', { class: 'bm-piano__panel', 'data-panel': true, id: 'bm-piano-panel' }, keysRow);
  const toggle = h(
    'button',
    { type: 'button', class: 'bm-piano__toggle', 'data-toggle': true, 'aria-expanded': 'true', 'aria-controls': 'bm-piano-panel' },
    'Piano',
  );
  const setExpanded = (open: boolean) => {
    toggle.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    if (!open) releaseAll();
  };
  toggle.addEventListener('click', () => setExpanded(panel.hidden));

  const el = h(
    'section',
    { class: 'bm-piano', 'aria-label': 'Piano' },
    h(
      'div',
      { class: 'bm-piano__head' },
      toggle,
      h(
        'span',
        { class: 'bm-piano__hint' },
        'Keys ',
        ...'a w s e d f t g y h u j k o l p ;'.split(' ').map((k) => h('kbd', null, k)),
        ' from C4 · ',
        h('kbd', null, 'Shift'),
        ' accent · plays lead; records while ● Record is armed',
      ),
    ),
    panel,
  );

  container.append(el);
  return {
    el,
    isActive,
    setExpanded,
    dispose() {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', releaseAll);
      releaseAll();
      el.remove();
    },
  };
}
